import { faSpinner } from '@fortawesome/pro-duotone-svg-icons';
import { faMinus, faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { hasPermission } from 'Components/Authorize';
import { addNotification } from 'Components/Notifications/actions';
import TipContent from 'Components/Tooltip';
import { Tip } from 'grommet';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { GlobalsContext } from 'Shared/context';
import { buildURL, get } from 'Shared/fetch';
import { trackMP } from 'Shared/mp';
import { Permission } from 'zl-shared';

import { getConfig, getDesktopOrMobile } from '@/helpers';
import { usePendingUGC } from '@/hooks';
import { toggleStreamWarningMsg } from '@/redux/actions';
import { useSelector } from '@/redux/helper';
import { selectHasExplorerFeature } from '@/redux/selectors/users';
import { decrementDailyCredit, setUserData } from '@/redux/slices/user/actions';

import { cameraZoomCreditCheckMessage } from '../../actions';
import style from './style.scss';

const initialZoomState = {
  currentZoom: null,
  requestedZoom: null,
};

const ZOOM_LEVELS = [100, 80, 60, 40, 20, 0];

const TooltipWrapper = ({ show, tooltip, children, horizontal }) => {
  if (!show) {
    return children;
  }

  return (
    <Tip dropProps={{ align: horizontal ? { bottom: 'top' } : { left: 'right' } }} content={tooltip} plain>
      {children}
    </Tip>
  );
};

const ZoomBar = ({ horizontal, width = '100%', height = '30px', className }) => {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user?.userId);
  const bannedUntil = useSelector((state) => state.user?.bannedUntil);
  const role = useSelector((state) => state.user?.role);
  const active = useSelector(selectHasExplorerFeature);
  const dailyCredits = useSelector((state) => state.user?.dailyCredits);
  const habitatId = useSelector((state) => state.habitat?.habitatInfo?._id);
  const configs = useSelector((state) => state.habitat?.habitatInfo?.selectedCamera?.configs);
  const cameraId = useSelector((state) => state.habitat?.habitatInfo?.selectedCamera?._id);
  const hostOnly = useSelector((state) => state.habitat?.habitatInfo?.hostOnly);
  const membersOnly = useSelector((state) => state.habitat?.habitatInfo?.membersOnly || {});
  const { socket } = useContext(GlobalsContext);
  const [zoomInfo, setZoomInfo] = useState(initialZoomState);
  const [isLoading, setIsLoading] = useState(false);
  const wrapperRef = useRef();
  const device = getDesktopOrMobile();

  const isPendingUGC = usePendingUGC();

  const onPropagateZoom = useCallback(
    ({ zoom }) => {
      setZoomInfo({ ...zoomInfo, currentZoom: zoom, requestedZoom: null });
      if (isLoading) {
        setIsLoading(false);
      }
    },
    [setZoomInfo, isLoading, zoomInfo],
  );

  const onZoomBlocked = ({ message, userId: id }) => {
    if (message && userId === id) {
      dispatch(toggleStreamWarningMsg(message));

      get(buildURL('/users/user'))
        .then((user) => dispatch(setUserData(user)))
        .catch((error) => console.error('Failed to re-fetch user object', error));

      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
    }
  };

  useEffect(() => {
    if (socket) {
      socket.on('propagateZoom', onPropagateZoom);
      socket.on('userSetZoomBlocked', onZoomBlocked);
    }
    return () => {
      if (socket) {
        socket.off('propagateZoom', onPropagateZoom);
        socket.off('userSetZoomBlocked', onZoomBlocked);
      }
    };
  }, [socket, onPropagateZoom]);

  const canBlockCamera = useMemo(() => hasPermission(Permission.Camera.Block), [role]);
  const clickBlockedByHostMode = useMemo(() => hostOnly?.enabled && !canBlockCamera, [hostOnly?.enabled, role]);
  const clickBlockedByMembersOnlyMode = useMemo(() => {
    const { enabled, validUntil } = membersOnly;
    return enabled && new Date(validUntil) > new Date() && role === 'user' && !active;
  }, [role, membersOnly?.enabled, membersOnly?.validUntil]);

  const onClick = (requestedZoom, currentZoom) => {
    if (isPendingUGC) {
      return;
    }

    if (bannedUntil && new Date(bannedUntil) > new Date()) {
      dispatch(
        addNotification({
          id: `banned-${Math.random()}`,
          type: 'toast',
          text: `You have been timed out and cannot use zoom controls until ${new Date(bannedUntil).toLocaleTimeString()}`,
          timeout: 6000,
        }),
      );

      return;
    }

    if (requestedZoom === currentZoom) {
      return;
    }

    if (clickBlockedByMembersOnlyMode) {
      dispatch(toggleStreamWarningMsg('Camera controls are currently disabled for free users'));
      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
      return;
    }

    if (clickBlockedByHostMode) {
      dispatch(toggleStreamWarningMsg('Camera is currently being controlled by host'));
      setTimeout(() => dispatch(toggleStreamWarningMsg(false)), 5000);
      return;
    }

    if (dailyCredits?.inUse && dailyCredits.cameraZoom === 0) {
      cameraZoomCreditCheckMessage(dailyCredits, dispatch);
      return;
    }

    setIsLoading(true);

    socket.emit('userSetZoom', {
      zoom: requestedZoom,
      userId,
      habitatId,
      cameraId,
      decCredit: dailyCredits?.inUse,
    });

    dispatch(decrementDailyCredit('cameraZoom'));
    setZoomInfo({ currentZoom, isLoading: true, requestedZoom });
    setTimeout(() => setIsLoading(false), getConfig(configs, 'ptu.votingTime')?.configValue);
    trackMP('zoom', {
      habitatId,
      cameraId,
      currentZoom,
      requestedZoom,
      zoomType: requestedZoom > currentZoom ? 'in' : 'out',
    });
  };

  return (
    <div ref={wrapperRef} className={clsx(style.zoomContainer, className)} style={{ width, height }}>
      <div
        className={clsx(style.zoomWrapper, {
          '!flex-row-reverse': horizontal,
        })}
      >
        <TooltipWrapper show={device !== 'mobile'} tooltip={<TipContent message="Zoom In" />} horizontal={horizontal}>
          <div
            className={clsx(style.iconWrapper, {
              '!h-full !w-auto !min-w-fit': horizontal,
              [style.disabled]: isPendingUGC || isLoading || zoomInfo.currentZoom === 100,
            })}
            onClick={() => onClick(zoomInfo.currentZoom + 20, zoomInfo.currentZoom)}
          >
            <FontAwesomeIcon icon={faPlus} />
          </div>
        </TooltipWrapper>
        <div className={clsx(style.levels, { '!flex-grow !flex-row-reverse': horizontal })}>
          {ZOOM_LEVELS.map((zLvl) => (
            <TooltipWrapper
              key={zLvl}
              show={device !== 'mobile'}
              tooltip={<TipContent message="Zoom Controls" />}
              horizontal={horizontal}
            >
              <div
                className={clsx(style.dotWrapper, {
                  // eslint-disable-next-line max-len
                  [style.active]: zoomInfo.currentZoom === zLvl || zoomInfo.requestedZoom === zLvl,
                  [style.disabled]: isPendingUGC || (isLoading && zoomInfo.requestedZoom !== zLvl),
                })}
                onClick={() => onClick(zLvl, zoomInfo.currentZoom)}
              >
                <span key={zLvl} className={style.dot} />
                {zoomInfo.requestedZoom === zLvl && isLoading && (
                  <div className={style.loader}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                  </div>
                )}
              </div>
            </TooltipWrapper>
          ))}
        </div>
        <TooltipWrapper show={device !== 'mobile'} tooltip={<TipContent message="Zoom Out" />} horizontal={horizontal}>
          <div
            className={clsx(style.iconWrapper, {
              '!h-full !w-auto !min-w-fit': horizontal,
              [style.disabled]: isPendingUGC || zoomInfo.currentZoom === 0 || isLoading,
            })}
            onClick={() => onClick(zoomInfo.currentZoom - 20, zoomInfo.currentZoom)}
          >
            <FontAwesomeIcon icon={faMinus} />
          </div>
        </TooltipWrapper>
      </div>
    </div>
  );
};

export default ZoomBar;
